/**
 * Cycles the homepage hero flavor scenes.
 *
 * The wipe itself is CSS: adding data-state="entering" to a scene animates its
 * bands. This element only decides when that happens, and it stops while the
 * hero is off screen or while the theme editor is re-rendering the section.
 */
class JoamFlavorSequence extends HTMLElement {
  connectedCallback() {
    this.scenes = Array.from(this.querySelectorAll('.joam-flavor-seq__scene'));
    if (this.scenes.length < 2) return;

    this.hold = parseInt(this.dataset.hold, 10) || 1200;
    this.wipe = parseInt(this.dataset.wipe, 10) || 520;
    this.current = 0;
    this.timer = null;
    this.paused = true;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.start();
        } else {
          this.stop();
        }
      });
    });
    this.observer.observe(this);

    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  handleVisibility = () => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };

  start() {
    if (!this.paused) return;
    this.paused = false;
    this.queueNext();
  }

  stop() {
    this.paused = true;
    clearTimeout(this.timer);
  }

  queueNext() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.advance(), this.hold);
  }

  advance() {
    if (this.paused) return;

    const from = this.scenes[this.current];
    const nextIndex = (this.current + 1) % this.scenes.length;
    const to = this.scenes[nextIndex];

    // Restart the CSS animation: removing and re-adding the attribute in
    // separate frames makes the browser treat it as a new animation.
    to.removeAttribute('data-state');
    requestAnimationFrame(() => {
      to.setAttribute('data-state', 'entering');

      this.timer = setTimeout(() => {
        from.removeAttribute('data-state');
        to.setAttribute('data-state', 'current');
        this.current = nextIndex;
        if (!this.paused) this.queueNext();
      }, this.wipe);
    });
  }
}

if (!customElements.get('joam-flavor-sequence')) {
  customElements.define('joam-flavor-sequence', JoamFlavorSequence);
}
